import { Category, ModernLineItem } from "../__generated__/graphql";

/**
 * Partner-facing SWO section header for a category.
 *
 * Partners read the SWO section header as the list of equipment on the order,
 * so for a multi-item category whose form name enumerates distinct,
 * non-interchangeable items (currently only "Hospital Bed & Lift") we name what
 * was actually ordered instead of the clinic-side form category name: a
 * bed-only order says "Hospital Bed", a lift-only order says "Patient Lift &
 * Sling", and an order with both keeps "Hospital Bed & Lift". Wording mirrors
 * the Progress Notes Summary (see ProgressNoteSummary/HospitalBedSection.tsx).
 *
 * Every other category falls back to its display name unchanged.
 */
export const getSwoCategoryHeader = (
  category: Pick<Category, "identifier" | "displayName">,
  lineItems: Pick<ModernLineItem, "formItemName">[],
): string => {
  if (category.identifier !== "HOSPITAL_BED") {
    return category.displayName;
  }

  // Mirrors HospitalBedSection.tsx: a "main bed" is any item that isn't a rail,
  // trapeze, or lift accessory; the lift is the "Patient lift and sling" item.
  const hasBed = lineItems.some((li) => {
    const name = li.formItemName.toLowerCase();
    return (
      !name.includes("rail") &&
      !name.includes("trapeze") &&
      !name.includes("lift")
    );
  });
  const hasLift = lineItems.some((li) =>
    li.formItemName.toLowerCase().includes("lift"),
  );

  if (hasBed && hasLift) {
    return "Hospital Bed & Lift";
  }
  if (hasLift) {
    return "Patient Lift & Sling";
  }
  return "Hospital Bed";
};
